import React, { memo } from 'react';
import { useFrame } from '~/shared/utils/frame';
import Script from 'next/script';
import { useChat } from '../useChat';
import { useZendesk } from './useZendesk';

const zendeskScriptUrl =
    'https://static.zdassets.com/ekr/snippet.js?key=ae0c4cd9-e86b-43de-ad9a-e8a5425661bd';

export const Zendesk = memo(() => {
    const { data } = useFrame();
    const { setButtonOffsetVertical } = useZendesk();
    const { setChatState, setChatError, showChat } = useChat();
    const handleZendeskScriptLoaded = () => {
        // When scripts is loaded, zE should be available on window
        const { zE } = window;
        if (!zE) {
            // Zendesk catches, we show notification
            const error = new Error('Could not find "zE" of Zendesk');
            setChatError(error);
            throw error;
        }
        // Settings
        zE('webWidget', 'setLocale', data?.culture || 'en-gb');
        zE(() => {
            // When all the other 8971263 scripts zendesk loads has loaded, $zopim should be available on window
            const { $zopim: zopim } = window;
            if (!zopim) {
                // Zendesk catches, we show notification
                const error = new Error('Could not find "$zopim" of Zendesk');
                setChatError(error);
                throw error;
            }

            zopim(() => {
                // Add listener on chat window hide and show
                // Move Zendesk button out of view, but keep chat in view by resetting offset when chat is open
                // (Yes, the button offset has an effect on the window offset for some reason)
                zopim.livechat.window.onHide(() => {
                    setButtonOffsetVertical(-200);
                    setChatState('closed');
                });
                zopim.livechat.window.onShow(() => {
                    setButtonOffsetVertical(0);
                    setChatState('open');
                });
                showChat();
            });
        });
    };

    const handleZendeskScriptError = (e: Error) => {
        setChatError(e);
    };

    return (
        <Script
            onLoad={handleZendeskScriptLoaded}
            onError={handleZendeskScriptError}
            id="ze-snippet"
            src={zendeskScriptUrl}
        />
    );
});
